import { useEffect } from 'react';
import { PRESENCE_PING_INTERVAL_MS, type UserStatus } from '@allemnii-pulse/shared';
import { api } from './api.js';

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/** After a failed ping, wait this long before trying again rather than retrying on every
 * pointer move. */
const RETRY_AFTER_FAILURE_MS = 60_000;

// The last successful ping is kept in localStorage so that several open tabs share one
// throttle instead of each pinging. Storage can be blocked, so every access is wrapped.
function lastPingKey(uid: string) {
  return `pulse.presencePing.${uid}`;
}

function readLastPing(uid: string): number {
  try {
    return Number(localStorage.getItem(lastPingKey(uid))) || 0;
  } catch {
    return 0;
  }
}

function writeLastPing(uid: string, at: number) {
  try {
    localStorage.setItem(lastPingKey(uid), String(at));
  } catch {
    // Without storage each tab throttles on its own, which only costs a few extra writes.
  }
}

/**
 * Tells the API you're still here (POST /me/presence) while you're using the app with a status
 * of online or busy. The expiry job sets you offline after STATUS_INACTIVITY_TIMEOUT_MS without
 * a ping. Only input in a visible tab counts, so a tab left open overnight doesn't keep you
 * online. Pings at most once every PRESENCE_PING_INTERVAL_MS across all your tabs, and never
 * while you're offline, since there's nothing to expire then. Choosing a status counts as
 * activity on the API side, so there's no ping when the status changes.
 */
export function usePresencePing(uid: string, status: UserStatus) {
  useEffect(() => {
    if (status === 'offline') return;

    // Kept in memory as well, so a pointer move costs a timestamp compare rather than a
    // storage read.
    let lastPing = 0;
    let inFlight = false;

    function onActivity() {
      if (inFlight || document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastPing < PRESENCE_PING_INTERVAL_MS) return;
      lastPing = Math.max(lastPing, readLastPing(uid));
      if (now - lastPing < PRESENCE_PING_INTERVAL_MS) return;

      inFlight = true;
      api
        .post('/me/presence')
        .then(() => {
          lastPing = now;
          writeLastPing(uid, now);
        })
        .catch(() => {
          lastPing = now - PRESENCE_PING_INTERVAL_MS + RETRY_AFTER_FAILURE_MS;
        })
        .finally(() => {
          inFlight = false;
        });
    }

    const opts = { capture: true, passive: true } as const;
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, opts);
    document.addEventListener('visibilitychange', onActivity);
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity, opts);
      document.removeEventListener('visibilitychange', onActivity);
    };
  }, [uid, status]);
}
